import { useState, useEffect, useRef, type ReactNode, type ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { captureError } from "@/lib/errorReporting";
import { TopBar, Icon, useToast } from "@/components/ui";
import Button from "@/components/ui/Button";
import cardBgSvg from "@/assets/shapes/card-bg.svg";
import dashedCirclePng from "@/assets/shapes/dashed-circle.png";
import { useWallet } from "@solana/wallet-adapter-react";
import { useConfig } from "@/providers";
import { processAndUploadImage, ImageUploadError } from "@/utils/imageUpload";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

interface FormData {
    name: string;
    ticker: string;
    lore: string;
    imageUrl: string;
}

const INITIAL: FormData = {
    name: "",
    ticker: "",
    lore: "",
    imageUrl: "",
};

/* ════════════════════════════════════════════
   CODEV a Coin — Create / Launch Page
   ════════════════════════════════════════════ */
export default function CodevCoinPage(): ReactNode {
    const navigate = useNavigate();
    const { publicKey } = useWallet();
    const { MIN_DEPOSIT_SOL, MAX_DEPOSIT_SOL, DEPOSIT_TARGET_SOL, UNLOCK_MCAP_SOL } = useConfig();
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState<FormData>(INITIAL);
    const [prefilling, setPrefilling] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const { toast } = useToast();
    const prefilledTickerRef = useRef<string | null>(null);

    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    /* ─── Auto-fill from ?coin= query param ─── */
    useEffect(() => {
        const coinSlug = searchParams.get("coin");
        if (!coinSlug) return;

        setPrefilling(true);
        fetch(`${API_BASE}/api/coins/${coinSlug}`)
            .then((r) => {
                if (!r.ok) throw new Error("Not found");
                return r.json();
            })
            .then((data) => {
                setForm({
                    name: data.name || "",
                    ticker: data.ticker || "",
                    lore: data.lore || "",
                    imageUrl: data.imageUrl || "",
                });
                if (data.ticker) prefilledTickerRef.current = data.ticker;
                if (data.imageUrl) setImagePreview(data.imageUrl);
            })
            .catch((err) => captureError("Prefill error:", err))
            .finally(() => setPrefilling(false));
    }, [searchParams]);

    function update(field: keyof FormData) {
        return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setForm((prev) => ({ ...prev, [field]: e.target.value }));
            setError(null);
        };
    }

    async function handleImageSelect(e: ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        setImagePreview(URL.createObjectURL(file));
        setImageError(null);
        setError(null);
        setImageUploading(true);

        try {
            const downloadUrl = await processAndUploadImage(file);
            setForm((prev) => ({ ...prev, imageUrl: downloadUrl }));
        } catch (err) {
            setImagePreview(null);
            setForm((prev) => ({ ...prev, imageUrl: "" }));
            setImageError(
                err instanceof ImageUploadError
                    ? err.message
                    : "Image upload failed. Please try again."
            );
        } finally {
            setImageUploading(false);
        }
    }

    function handleImageRemove() {
        setImagePreview(null);
        setForm((prev) => ({ ...prev, imageUrl: "" }));
        setImageError(null);
    }

    async function handleSubmit() {
        if (!form.name.trim() || !form.ticker.trim()) {
            setError("Name and ticker are required");
            return;
        }

        if (!form.imageUrl) {
            setError("Please upload a coin image");
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const finalTicker = form.ticker.trim().replace(/^\$+/, "");
            const normCurrent = finalTicker.toUpperCase();
            const normPrefilled = prefilledTickerRef.current?.replace(/^\$/, "").toUpperCase();
            const cabalType = normPrefilled && normCurrent === normPrefilled ? "vamp" : "create";

            const res = await fetch(`${API_BASE}/api/cabals`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    ticker: finalTicker,
                    lore: form.lore.trim() || undefined,
                    imageUrl: form.imageUrl || undefined,
                    type: cabalType,
                    minDeposit: MIN_DEPOSIT_SOL,
                    maxDeposit: MAX_DEPOSIT_SOL,
                    depositTarget: DEPOSIT_TARGET_SOL,
                    targetMcap: UNLOCK_MCAP_SOL,
                    leaderWallet: publicKey?.toBase58() || "mock-wallet-address",
                    leaderDeposit: 0,
                }),
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || "Failed to CODEV coin");
            }

            const cabal = await res.json();
            navigate(`/codevs/${cabal.slug}/deposit`, { state: { isLeader: true } });
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Something went wrong";
            setError(msg);
            toast(msg, "error");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            <TopBar
                variant="minimal"
                showBack
                onBack={() => navigate(-1)}
                title="Create Coin"
            />

            <main className="relative z-10 flex-1 flex flex-col gap-[clamp(16px,2.6dvh,24px)] px-[clamp(16px,2.6dvh,24px)] pb-[clamp(80px,10.7dvh,112px)]">
                {/* ─── Dashed circle + upload (in flow, overlaps card below) ─── */}
                <div className="flex flex-col items-center mt-2 mb-[clamp(-45px,-5.9dvh,-55px)] z-20 relative">
                    <label className="relative cursor-pointer block">
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            className="hidden"
                            onChange={handleImageSelect}
                            disabled={imageUploading}
                        />
                        <div className="relative w-[clamp(85px,11.7dvh,109px)] h-[clamp(85px,11.7dvh,109px)] flex items-center justify-center group">
                            <img
                                src={dashedCirclePng}
                                alt=""
                                className="absolute inset-0 w-full h-full pointer-events-none"
                                draggable={false}
                            />
                            {imagePreview ? (
                                <div className="w-[82%] h-[82%] rounded-full overflow-hidden relative">
                                    <img
                                        src={imagePreview}
                                        alt="Logo preview"
                                        className="w-full h-full object-contain"
                                    />
                                    {imageUploading && (
                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Icon name="add-photo" size={28} className="opacity-60 group-hover:opacity-80 transition-opacity" />
                            )}
                            {imagePreview && !imageUploading && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleImageRemove(); }}
                                    className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#222] border border-white/20 flex items-center justify-center hover:bg-[#333] transition-colors z-10"
                                >
                                    <span className="text-white/70 text-xs leading-none">✕</span>
                                </button>
                            )}
                        </div>
                    </label>

                    {imageError && (
                        <p className="mt-1 text-[10px] text-red-400 font-semibold text-center max-w-[160px]">
                            {imageError}
                        </p>
                    )}
                </div>

                {/* ─── Form Card with SVG background (notch cutout from Figma) ─── */}
                <div className="relative h-[clamp(380px,51dvh,476px)]">
                    <img
                        src={cardBgSvg}
                        alt=""
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        draggable={false}
                    />
                    <div className="relative px-[16px] pt-[clamp(12px,1.7dvh,16px)] pb-[clamp(12px,1.7dvh,16px)] flex flex-col gap-[clamp(12px,2.1dvh,20px)]">
                        {/* Spacer for the notch area at top of card */}
                        <div className="h-[clamp(8px,1.5dvh,14px)]" />

                        {prefilling ? (
                            <div className="flex items-center justify-center py-6 gap-3">
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                                <span className="text-xs font-semibold text-white/40 uppercase tracking-widest">
                                    Loading coin...
                                </span>
                            </div>
                        ) : (
                            <>
                                <InputField
                                    label="NAME"
                                    placeholder="e.g. Ser PEPE"
                                    value={form.name}
                                    onChange={update("name")}
                                />
                                <InputField
                                    label="Ticker"
                                    placeholder="PEPE"
                                    value={form.ticker}
                                    onChange={update("ticker")}
                                    className="uppercase"
                                />

                                <div className="flex flex-col gap-[clamp(6px,0.9dvh,8px)]">
                                    <label className="text-[12px] font-semibold uppercase text-white/50">
                                        Lore
                                    </label>
                                    <textarea
                                        className="w-full h-[clamp(120px,19.3dvh,180px)] rounded-[8px] p-[16px] text-[18px] font-semibold resize-none bg-[#0a0a0a] border-[0.5px] border-white/20 text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-all"
                                        placeholder="Describe the coin's vision...."
                                        value={form.lore}
                                        onChange={update("lore")}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ─── Info Note ─── */}
                <div className="flex gap-2 items-start p-[clamp(14px,2.1dvh,20px)] rounded-[12px] border border-[rgba(153,153,153,0.5)] bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] overflow-hidden">
                    <Icon name="info-circle" size={20} className="flex-shrink-0 mt-0.5" />
                    <p className="text-[14px] font-medium leading-[20px] text-white">
                        <span className="text-[#f3ce28]">Note: </span>
                        The coin launches after the launch pool hits 20 SOL. Coins unlock after graduation.
                    </p>
                </div>

                {error && (
                    <p className="text-red-400 text-xs font-semibold text-center mt-4">{error}</p>
                )}
            </main>

            {/* ─── Bottom CTA ─── */}
            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 py-[clamp(12px,1.7dvh,20px)] px-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent">
                <Button
                    variant="cta"
                    size="lg"
                    textStyle="v2"
                    fullWidth
                    loading={submitting}
                    disabled={imageUploading || submitting}
                    onClick={handleSubmit}
                    className="max-w-[400px] mx-auto text-[clamp(18px,2.1dvh,20px)]"
                >
                    Initiate Launch Pool
                </Button>
            </div>
        </div>
    );
}

/* ─── Reusable Input Field ─── */
function InputField({
    label,
    placeholder,
    value,
    onChange,
    className = "",
}: {
    label: string;
    placeholder: string;
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    className?: string;
}): ReactNode {
    return (
        <div className="flex flex-col gap-[clamp(6px,0.9dvh,8px)]">
            <label className="text-[12px] font-semibold leading-normal uppercase text-white/50">
                {label}
            </label>
            <input
                type="text"
                className={`w-full h-[clamp(44px,6dvh,56px)] rounded-[8px] px-[18px] text-[18px] font-semibold bg-[#0a0a0a] border-[0.5px] border-white/20 text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-all ${className}`}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
            />
        </div>
    );
}
